import { useEffect, useRef } from 'react'
import { socketSingleton } from 'services/ws'
import { ChatWebSocketMessage, TicketStatusUpdateBody, WebsocketActionsEnum } from 'types/index'
import { ChatMessage, MessageTypeEnum } from 'types/chat-message'
import { Chat, ChatResponseModeEnum } from 'types/chat'
import { store } from '@/store'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsActions } from 'store/settings'
import { markChatAsRead } from 'api/chats'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { audioPlayer } from 'services/audio-player'

interface UseChatWebSocketOptions {
  chatId: string | undefined
  userId: string | undefined
  currentChatBotId: string | undefined
  currentUserId: string | undefined
  numberOfQuestionsBeforeAskTicketOpen: number
}

/**
 * Hook to manage WebSocket rooms for chat functionality.
 * Joins the user room (UPDATE_CHAT_PARTICIPANTS) and the chat room
 * (NEW_MESSAGE, STREAM, END_STREAM); events arrive named after the action.
 */
export const useChatWebSocket = ({
  chatId,
  userId,
  currentChatBotId,
  currentUserId,
  numberOfQuestionsBeforeAskTicketOpen,
}: UseChatWebSocketOptions) => {
  const botResponseCountRef = useRef(0)
  const { isAppMode } = useGetBotMode()

  // User room - personal notifications (chat participant updates)
  useEffect(() => {
    if (!userId) return
    const room = String(userId)

    const onUpdateParticipants = (wsMessage: ChatWebSocketMessage) => {
      const updatedChat = wsMessage.body as Chat
      store.dispatch(messagesActions.setChat(updatedChat))
    }

    socketSingleton.join(room)
    socketSingleton.on(WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS, onUpdateParticipants)

    return () => {
      socketSingleton.off(WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS, onUpdateParticipants)
      socketSingleton.leave(room)
    }
  }, [userId])

  // Chat room - messages and bot response streaming
  useEffect(() => {
    if (!chatId) return
    const room = String(chatId)

    // Reset bot response count when chat changes
    botResponseCountRef.current = 0

    const onNewMessage = (wsMessage: ChatWebSocketMessage) => {
      const message = wsMessage.body as ChatMessage
      // action events are shared across rooms: keep only this chat's messages
      if (message?.chatId !== chatId) return

      // System messages (ticket closed/reopened) have no senderId and must always be shown
      if (message?.type === MessageTypeEnum.SYSTEM) {
        store.dispatch(messagesActions.addIncomingMessage(message))
        store.dispatch(
          messagesActions.updateChat({
            lastMessage: message,
            _id: message.chatId,
          }),
        )
        return
      }

      if (message?.chatResponseMode === ChatResponseModeEnum.OPERATOR) {
        // Add message only if it's from a different user
        if (currentUserId && message.senderId !== currentUserId) {
          store.dispatch(messagesActions.addIncomingMessage(message))
        }
        store.dispatch(
          messagesActions.updateChat({
            lastMessage: message,
            _id: message.chatId,
          }),
        )
        if (isAppMode || store.getState().widgetState.isOpened) {
          markChatAsRead(chatId).catch(console.error)
        }
      }
    }

    // Auto-generated title arrives on the chat room (no chatId in body): the joined chat room
    // is the current chat, so merge the name into it; the chat list row picks it up immediately.
    const onChatNameSet = (wsMessage: ChatWebSocketMessage) => {
      const name = wsMessage.body?.name
      if (name) store.dispatch(messagesActions.updateCurrentChat({ name }))
    }

    // The stream payload carries no chatId: it belongs to this room's chat, the one this hook
    // is subscribed to. Write that chat's slice and mark it as streaming (dots off, spinner on).
    const onStream = (wsMessage: ChatWebSocketMessage) => {
      if (currentChatBotId) {
        // The server emits zero-width spaces (U+200B) as keep-alive pings while the answer is
        // still being generated. Strip them: a body that is only keep-alive carries no real
        // content, so it must not show a bubble nor end the loading dots (keep 'awaitingBot').
        const body = (wsMessage.body ?? '').replace(/\u200B/g, '')
        if (!body) return
        store.dispatch(
          messagesActions.upsertBotMessage({
            chatId,
            botMessageId: wsMessage.messageId,
            partialResponse: body,
            senderId: currentChatBotId,
          }),
        )
        store.dispatch(messagesActions.setChatStatus({ chatId, status: 'streaming' }))
      }
    }

    const onEndStream = () => {
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
      setTimeout(() => {
        store.dispatch(messagesActions.updateChatLastMessage())
      }, 0)

      // Check if we should show the ticket reminder
      const state = store.getState()
      const isOperatorMode = messagesSelectors.currentChatIsInOperatorMode(state)
      if (!isOperatorMode && numberOfQuestionsBeforeAskTicketOpen > 0) {
        botResponseCountRef.current += 1
        if (botResponseCountRef.current % numberOfQuestionsBeforeAskTicketOpen === 0) {
          store.dispatch(settingsActions.setTicketReminderVisible(true))
        }
      }
    }

    // GENERATING starts the thinking indicator, READY replaces it with the questions;
    // neither fires for agentic / no-retrieval answers or when the tenant flag is off.
    const onDynamicQuestionsGenerating = (wsMessage: ChatWebSocketMessage) => {
      store.dispatch(messagesActions.setDynamicQuestionsGenerating({ chatId, messageId: wsMessage.messageId }))
    }

    const onDynamicQuestions = (wsMessage: ChatWebSocketMessage) => {
      const questions = Array.isArray(wsMessage.body)
        ? wsMessage.body.filter((question): question is string => typeof question === 'string')
        : []

      if (questions.length === 0) {
        store.dispatch(messagesActions.clearDynamicQuestionsGenerating({ chatId }))
        return
      }
      store.dispatch(
        messagesActions.setMessageDynamicQuestions({
          chatId,
          messageId: wsMessage.messageId,
          questions,
        }),
      )
    }

    // TTS audio, two sources on the same events: live voice answers (chat room, with seq)
    // and message replay (room = messageId, no seq: the player appends in arrival order).
    // Segments can be missing entirely (server TTS failure): end_stream_audio may arrive alone.
    const onStreamAudio = (wsMessage: ChatWebSocketMessage) => {
      const seq = (wsMessage as unknown as { seq?: number }).seq
      const base64 = typeof wsMessage.body === 'string' ? wsMessage.body : ''
      if (!base64 || !wsMessage.messageId) return
      audioPlayer.enqueue(wsMessage.messageId, seq, base64)
    }

    const onEndStreamAudio = (wsMessage: ChatWebSocketMessage) => {
      if (!wsMessage.messageId) return
      audioPlayer.endOfStream(wsMessage.messageId)
      // Replay rooms are per-message (joined on the listen tap): closed here, at end of stream.
      if (wsMessage.messageId !== chatId) socketSingleton.leave(wsMessage.messageId)
    }

    // Keep the ticket badge/state in sync without refetching (covers the reopen case too)
    const onTicketStatusUpdate = (wsMessage: ChatWebSocketMessage) => {
      const body = wsMessage.body as TicketStatusUpdateBody
      if (body?.chatId !== chatId) return
      store.dispatch(messagesActions.setTicketStatus(body))
    }

    socketSingleton.join(room)
    socketSingleton.on(WebsocketActionsEnum.NEW_MESSAGE, onNewMessage)
    socketSingleton.on(WebsocketActionsEnum.CHAT_NAME_SET, onChatNameSet)
    socketSingleton.on(WebsocketActionsEnum.STREAM, onStream)
    socketSingleton.on(WebsocketActionsEnum.END_STREAM, onEndStream)
    socketSingleton.on(WebsocketActionsEnum.STREAM_AUDIO, onStreamAudio)
    socketSingleton.on(WebsocketActionsEnum.END_STREAM_AUDIO, onEndStreamAudio)
    socketSingleton.on(WebsocketActionsEnum.DYNAMIC_QUESTIONS_GENERATING, onDynamicQuestionsGenerating)
    socketSingleton.on(WebsocketActionsEnum.DYNAMIC_QUESTIONS_READY, onDynamicQuestions)
    socketSingleton.on(WebsocketActionsEnum.TICKET_STATUS_UPDATE, onTicketStatusUpdate)

    return () => {
      socketSingleton.off(WebsocketActionsEnum.NEW_MESSAGE, onNewMessage)
      socketSingleton.off(WebsocketActionsEnum.CHAT_NAME_SET, onChatNameSet)
      socketSingleton.off(WebsocketActionsEnum.STREAM, onStream)
      socketSingleton.off(WebsocketActionsEnum.END_STREAM, onEndStream)
      socketSingleton.off(WebsocketActionsEnum.STREAM_AUDIO, onStreamAudio)
      socketSingleton.off(WebsocketActionsEnum.END_STREAM_AUDIO, onEndStreamAudio)
      socketSingleton.off(WebsocketActionsEnum.DYNAMIC_QUESTIONS_GENERATING, onDynamicQuestionsGenerating)
      socketSingleton.off(WebsocketActionsEnum.DYNAMIC_QUESTIONS_READY, onDynamicQuestions)
      socketSingleton.off(WebsocketActionsEnum.TICKET_STATUS_UPDATE, onTicketStatusUpdate)
      socketSingleton.leave(room)
      // Leaving the chat (switch or unmount) must silence its TTS: the player is global
      // and would otherwise keep talking over the next chat.
      audioPlayer.stopAll()
    }
  }, [chatId, currentUserId, currentChatBotId, numberOfQuestionsBeforeAskTicketOpen])
}
